/**
 * DialogueManager
 * Handles contextual radio calls, character quotes, and the comms feed
 */
export class DialogueManager {
  constructor(game) {
    this.game = game;
    this.queue = [];
    this.isSpeaking = false;
    this.currentDialogue = null;
    
    // Dialogue data mapping characters to situational quotes
    this.dialogueDatabase = {
      honeybadger: {
        intro: "RAGEBURROW Mk IV online. Let's break some things.",
        ram_success: "Why are brakes even legal?",
        ram_fail: "Tch. Missed the impact.",
        boost_start: "Maximum velocity is the only velocity!",
        take_damage: "Is that all you've got? I've had worse papercuts!",
        victory: "Contract fulfilled. Now, where's my payment?",
        generic: [
          "I'll land on that asteroid. Backwards.",
          "The explosion was a feature, not a bug.",
          "Autopilot? More like auto-pilot-error.",
        ]
      },
      octopus: {
        intro: "MULTIVEIL active. Calculating optimal victory vectors.",
        drone_deploy: "Expanding the network.",
        take_damage: "Inefficient. My shields are recalibrating.",
        victory: "Outcome predicted. Equity secured.",
        generic: [
          "Interesting. Continue.",
          "Let's explore governance options first.",
          "I've already acquired the company that makes your missiles.",
          "We should discuss equity before the explosion.",
        ]
      },
      elephant: {
        intro: "GRAY MATTER operational. Proceeding with methodical precision.",
        charge_max: "Simulations complete. Impact is inevitable.",
        take_damage: "Within acceptable loss margins.",
        victory: "Project completed. Documentation filed.",
        generic: [
          "Have we modeled second-order effects?",
          "Please don't.",
          "According to section 47-B, this is inadvisable.",
          "I've run the simulation 10,000 times. We die in 9,999.",
        ]
      },
      mantisshrimp: {
        intro: "CHROMA WAR engaged. Perceiving the unseen.",
        stealth_on: "I am but a ghost in the machine.",
        take_damage: "A temporary ripple in the void.",
        victory: "The cycle completes. As foretold.",
        generic: [
          "You are seeing only three dimensions of this problem.",
          "The black hole is merely a metaphor.",
          "Let me explain why you've already lost.",
          "This quarter's projections look challenging.",
        ]
      },
      ant: {
        intro: "FULFILLMENT QUEEN online. Optimizing logistics.",
        swarm_max: "Supply chain at 100% capacity.",
        take_damage: "Efficiency drop detected. Correcting...",
        victory: "Delivery confirmed. Transaction closed.",
        generic: [
          "Package delivered.",
          "I've listed nearby condos around it.",
          "Your defeat has been Prime-shipped.",
          "I already own the company that made your weapon.",
        ]
      },
      interceptorcaptain: {
        intro: '"MARKET OPEN. COMPLIANCE IS MANDATORY."',
        take_damage: '"Accounting discrepancy detected. Correcting."',
        generic: [
          '"Your paperwork is incomplete. So will you be."',
          '"Remain stationary for inspection."',
        ],
      },
      shippingboss: {
        intro: '"SHIPPING LANE: SEIZED. ASSETS: RECOVERED."',
        take_damage: '"Insurance claims are processed after liquidation."',
        generic: [
          '"Return the cargo. Return your life."',
          '"Convoy escort online. Target acquired."',
        ],
      },
      volatilityboss: {
        intro: '"VOLATILITY IS A FEATURE. YOU ARE THE BUG."',
        take_damage: '"Price discovery in progress. Your value is dropping."',
        generic: [
          '"The spread widens. So does the blast radius."',
          '"You cannot hedge this."',
        ],
      },
      hostileboss: {
        intro: '"HOSTILE MARKET CONDITIONS DETECTED. YOU ARE LIQUIDITY."',
        take_damage: '"A temporary drawdown. I will recover."',
        generic: [
          '"Exit liquidity is not a place. It is a person."',
          '"You are the last bidder. And you lose."',
        ],
      },
      ledgerboss: {
        intro: '"LEDGER CORE ONLINE. BALANCE RESTORATION INITIATED."',
        take_damage: '"The ledger will balance. With or without you."',
        generic: [
          '"Your life is a rounding error."',
          '"Auditing your hull integrity."',
        ],
      },
      liquidationboss: {
        intro: '"LIQUIDATION EVENT: EXECUTION START."',
        take_damage: '"All assets trend to zero. Including you."',
        generic: [
          '"Everything gets liquidated."',
          '"You cannot outrun a margin call that big."',
        ],
      },
      marketmakeromega: {
        intro: "Price discovery has been suspended.\nYour valuation approaches zero.",
        take_damage: '"Your value is dropping. Efficiently."',
        generic: [
          '"Bid rejected."',
          '"Liquidity is a privilege."',
          '"Spread widening. Outcome narrowing."',
        ],
      },
    };
  }

  /**
   * Trigger a specific contextual quote
   */
  trigger(characterId, triggerKey) {
    const charData = this.dialogueDatabase[characterId];
    if (!charData) return;

    const text = charData[triggerKey];
    if (text) {
      this.speak(characterId, text);
    }
  }

  /**
   * Trigger a random generic quote
   */
  triggerRandom(characterId) {
    const charData = this.dialogueDatabase[characterId];
    if (!charData || !charData.generic) return;

    const quotes = charData.generic;
    const text = quotes[Math.floor(Math.random() * quotes.length)];
    this.speak(characterId, text);
  }

  /**
   * Add dialogue to queue and process it
   */
  speak(characterId, text) {
    const dialogue = {
      characterId,
      text,
      timestamp: Date.now()
    };

    this.queue.push(dialogue);
    
    // Send to UI immediately as a "comms feed" entry
    if (this.game.uiManager && this.game.uiManager.addCommsEntry) {
      this.game.uiManager.addCommsEntry(characterId, text);
    }

    if (!this.isSpeaking) {
      this.processQueue();
    }
  }

  async processQueue() {
    if (this.queue.length === 0) {
      this.isSpeaking = false;
      return;
    }

    this.isSpeaking = true;
    this.currentDialogue = this.queue.shift();

    // Here we would trigger the actual audio playback via SoundManager
    if (this.game.soundManager && this.game.soundManager.playDialogue) {
      await this.game.soundManager.playDialogue(this.currentDialogue.characterId, this.currentDialogue.text);
    }

    // Cooldown between dialogues to prevent overlapping
    setTimeout(() => {
      this.processQueue();
    }, 3000);
  }

  dispose() {
    this.queue = [];
  }
}
